import React, { useEffect, useState } from 'react';
import styles from './PlanItemsEditor.module.css';
import { apiFetch } from '../api/client';
import { supabase } from '../auth/supabase';
import ToothChart from './ToothChart';

const PULPAL_OPTIONS = [
  'NA',
  'Normal pulp', 'Reversible pulpitis', 'Symptomatic irreversible pulpitis', 'Asymptomatic irreversible pulpitis',
  'Pulp necrosis', 'Previously initiated therapy', 'Previously treated'
];
const APICAL_OPTIONS = [
  'NA',
  'Normal apical tissues', 'Symptomatic apical periodontitis', 'Asymptomatic apical periodontitis',
  'Acute apical abscess', 'Chronic apical abscess', 'Condensing osteitis'
];

function isLowerJaw(tooth) {
  const t = parseInt(tooth, 10);
  const q = Math.floor(t / 10);
  return q === 3 || q === 4;
}

function isAnterior(tooth) {
  const u = parseInt(tooth, 10) % 10;
  return u >= 1 && u <= 3; // central, lateral, canine
}

function getSurfaceOptions(tooth) {
  const lower = isLowerJaw(tooth);
  const anterior = isAnterior(tooth);
  const list = [
    'Mesial',
    'Buccal',
    'Distal',
    lower ? 'Lingual' : 'Palatal',
    anterior ? 'Incisal' : 'Occlusal',
  ];
  return list;
}

function suggestSurfacesForTooth(tooth) {
  // Default none selected; caller may choose to prefill later
  return [];
}

export default function PlanItemsEditor({ planId }) {
  const [lookups, setLookups] = useState(null);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // {id,tooth,diagnosis_id,pulpal_dx,apical_dx,procedure_id,status_id,notes}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const lk = await apiFetch('/lookups', {}, getToken);
        const it = await apiFetch(`/plans/${planId}/items`, {}, getToken);
        setLookups(lk);
        setRows(it);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [planId]);

  const removeLocalIndex = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i).map((r, i2) => ({ ...r, sort_order: i2 })));

  const saveOne = async (draft) => {
    setSaving(true);
    try {
      if (!draft.tooth || (!draft.diagnosis_id && !draft.procedure_id)) throw new Error('Tooth and at least Diagnosis or Tx are required');
      if (!draft.id) {
        const created = await apiFetch(`/plans/${planId}/items`, { method: 'POST', body: {
          tooth: parseInt(draft.tooth, 10),
          diagnosis_id: draft.diagnosis_id ? parseInt(draft.diagnosis_id, 10) : null,
          pulpal_dx: draft.pulpal_dx || null,
          apical_dx: draft.apical_dx || null,
          procedure_id: draft.procedure_id ? parseInt(draft.procedure_id, 10) : null,
          status_id: draft.status_id ? parseInt(draft.status_id, 10) : null,
          diagnosis_text_override: draft.diagnosis_text_override || null,
          notes: draft.notes || null,
          sort_order: rows.length,
          meta: draft.meta || null,
        }}, getToken);
        setRows((prev) => [...prev, created]);
      } else {
        await apiFetch(`/plan-items/${draft.id}`, { method: 'PUT', body: {
          tooth: draft.tooth ? parseInt(draft.tooth, 10) : null,
          diagnosis_id: draft.diagnosis_id ? parseInt(draft.diagnosis_id, 10) : null,
          pulpal_dx: draft.pulpal_dx || null,
          apical_dx: draft.apical_dx || null,
          procedure_id: draft.procedure_id ? parseInt(draft.procedure_id, 10) : null,
          status_id: draft.status_id ? parseInt(draft.status_id, 10) : null,
          diagnosis_text_override: draft.diagnosis_text_override || null,
          notes: draft.notes || null,
          meta: draft.meta || null,
        }}, getToken);
        setRows((prev) => prev.map(r => r.id === draft.id ? { ...r, ...draft } : r));
      }
      setModal(null);
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.info}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  const nameById = (list) => Object.fromEntries((list || []).map(x => [String(x.id), x.name]));
  const diagName = nameById(lookups?.diagnoses || []);
  const procName = nameById(lookups?.procedures || []);
  const statusName = nameById(lookups?.statuses || []);

  const parseDiagnosesOverride = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);
  const parseSteps = (s) => (s || '').split('->').map(x => x.trim()).filter(Boolean);

  const openModalForTooth = (tooth) => {
    const existing = rows.find(r => String(r.tooth) === String(tooth)) || { tooth: String(tooth) };
    const extraDiag = parseDiagnosesOverride(existing.diagnosis_text_override).filter(nm => nm !== diagName[String(existing.diagnosis_id)]);
    // Treat notes as additional steps only; strip a lone primary if it was saved earlier
    const basePrimary = (existing.procedure_id ? procName[String(existing.procedure_id)] : '') || '';
    const steps = parseSteps(existing.notes).filter(nm => nm !== basePrimary);
    const meta = existing.meta || {};
    const defaultSurfaces = suggestSurfacesForTooth(String(tooth));
    setModal({
      id: existing.id || null,
      tooth: String(tooth),
      diagnosis_id: existing.diagnosis_id ? String(existing.diagnosis_id) : '',
      pulpal_dx: existing.pulpal_dx || '',
      apical_dx: existing.apical_dx || '',
      procedure_id: existing.procedure_id ? String(existing.procedure_id) : '',
      status_id: existing.status_id ? String(existing.status_id) : '',
      notes: existing.notes || '',
      extraDiagnoses: extraDiag, // labels
      steps: steps, // labels sequence
      // new meta-backed fields
      surfaces: Array.isArray(meta.surfaces) ? meta.surfaces : [],
      palpation: meta.palpation || '', // '+ve' | '-ve' | ''
      percussion: meta.percussion || '',
      cold_test: meta.cold_test || '',
      endoSlide: !!(meta.endo_assessment_slide || meta['endo assessment slide']),
    });
  };

  return (
    <div className={styles.card}>
      <div style={{ marginBottom: 10 }}>
        <ToothChart
          selected={rows.map((r) => parseInt(r.tooth || 0, 10)).filter(Boolean)}
          onSelect={openModalForTooth}
        />
      </div>
      <div className={styles.summary}>
        <div className={styles.summaryHead}>
          <div>Tooth</div>
          <div>Findings</div>
          <div>Proposed Tx</div>
          <div>Status</div>
        </div>
        {[...rows].sort((a,b)=> fdiRank(a.tooth)-fdiRank(b.tooth)).map((r, idx) => (
          <div key={r.id || idx} className={styles.summaryRow} onClick={() => openModalForTooth(r.tooth)} style={{ cursor: 'pointer' }}>
            <div>{r.tooth || '-'}</div>
            <div className={styles.findings}>{(() => {
              const primary = diagName[String(r.diagnosis_id)] || '';
              const extras = (r.diagnosis_text_override || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .filter(nm => !primary || nm !== primary);
              return (
                <>
                  <div className={styles.primaryLine}>
                    {(() => {
                      const meta = r.meta || {};
                      const surfaces = Array.isArray(meta.surfaces) ? meta.surfaces : [];
                      const abr = surfaces.map(s => (s || '').toString().charAt(0).toUpperCase()).join('');
                      const diag = (primary || '-') + (extras.length ? `, ${extras.join(', ')}` : '');
                      return abr ? `(${abr}) ${diag}` : diag;
                    })()}
                  </div>
                  {(() => {
                    const m = r.meta || {};
                    const bits = [];
                    if (m.palpation) bits.push(`Palpation ${m.palpation}`);
                    if (m.percussion) bits.push(`Percussion ${m.percussion}`);
                    if (m.cold_test) bits.push(`Cold ${m.cold_test}`);
                    return bits.length ? (<div className={styles.assessLine}>{bits.join('; ')}</div>) : null;
                  })()}
                  <div className={styles.dxLine}>
                    {r.pulpal_dx ? (<span className={`${styles.badge} ${styles.badgePulpal}`}>Pulpal: {r.pulpal_dx}</span>) : null}
                    {r.apical_dx ? (<span className={`${styles.badge} ${styles.badgeApical}`}>Apical: {r.apical_dx}</span>) : null}
                  </div>
                </>
              );
            })()}</div>
            <div>{(() => {
              const primary = procName[String(r.procedure_id)] || '-';
              const steps = (r.notes || '').split('->').map(x => x.trim()).filter(Boolean);
              if (!steps.length) return primary;
              if (steps.length === 1 && steps[0] === primary) return primary;
              return [primary, ...steps].join(' -> ');
            })()}</div>
            <div>{statusName[String(r.status_id)] || '-'}</div>
          </div>
        ))}
      </div>

      {modal && (
        <div className={styles.overlay} onClick={() => !saving && setModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>Tooth {modal.tooth}</div>
            

            <div className={styles.label}>Surfaces included</div>
            <div className={styles.surfacesFull}>
              {getSurfaceOptions(modal.tooth).map((s) => (
                <button key={s} type="button" className={modal.surfaces.includes(s) ? styles.surfaceSelected : styles.surface}
                  onClick={() => {
                    const set = new Set(modal.surfaces);
                    if (set.has(s)) set.delete(s); else set.add(s);
                    setModal({ ...modal, surfaces: Array.from(set) });
                  }}>{s}</button>
              ))}
            </div>

            <div className={styles.label}>Clinical findings</div>
            <div className={styles.modalRow}>
              <select className={styles.select} value={modal.diagnosis_id} onChange={async (e) => {
                const val = e.target.value;
                if (val === '__create_diagnosis__') {
                  const name = prompt('New diagnosis name');
                  if (name && name.trim()) {
                    try {
                      const created = await apiFetch('/lookups/diagnoses', { method: 'POST', body: { name } }, getToken);
                      setLookups((prev) => ({ ...prev, diagnoses: [...prev.diagnoses, created] }));
                      setModal({ ...modal, diagnosis_id: String(created.id) });
                    } catch (err) { alert(err.message || 'Failed to create'); }
                  }
                  return;
                }
                setModal({ ...modal, diagnosis_id: val });
              }}>
                <option value="">Diagnosis</option>
                {(lookups?.diagnoses || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                <option value="__create_diagnosis__">Create…</option>
              </select>
              <select className={styles.select} value={modal.status_id} onChange={(e)=> setModal({ ...modal, status_id: e.target.value })}>
                <option value="">Status</option>
                {(lookups?.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className={styles.label}>Additional findings</div>
            <div className={styles.modalRow}>
              <select className={styles.select} onChange={(e)=>{ const id=e.target.value; if(!id) return; if(id==='__create_diagnosis__'){ const name=prompt('New finding'); if(name&&name.trim()){ (async()=>{ try{ const created=await apiFetch('/lookups/diagnoses',{ method:'POST', body:{ name }}, getToken); setLookups(prev=>({...prev, diagnoses:[...prev.diagnoses, created]})); const nm = created.name; if(nm && !modal.extraDiagnoses.includes(nm)) setModal({...modal, extraDiagnoses:[...modal.extraDiagnoses, nm]}); }catch(err){ alert(err.message||'Failed to create'); } })(); } e.target.value=''; return;} const nm=diagName[id]; if(nm && !modal.extraDiagnoses.includes(nm)) setModal({...modal, extraDiagnoses:[...modal.extraDiagnoses, nm]}); e.target.value=''; }}>
                <option value="">+ Add finding…</option>
                {(lookups?.diagnoses || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                <option value="__create_diagnosis__">Create…</option>
              </select>
              <div />
            </div>
            <div className={styles.chips}>
              {modal.extraDiagnoses.map((nm,idx)=> (
                <span key={idx} className={styles.chip}>{nm}<button className={styles.chipBtn} onClick={()=> setModal({...modal, extraDiagnoses: modal.extraDiagnoses.filter((x,i)=> i!==idx)})}>×</button></span>
              ))}
            </div>

            <div className={styles.endoHeader}>
              <div className={styles.label}>Endodontic Assessment</div>
              <div className={styles.label}>Endodontic Dx</div>
            </div>
            <div className={styles.endoGrid}>
              <div>
                <div className={styles.splitRow}>
                  <div className={styles.surfaceStatic}>Palpation</div>
                  <div className={styles.splitRight}>
                    <button className={modal.palpation==='-ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, palpation: '-ve' })}>-ve</button>
                    <button className={modal.palpation==='+ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, palpation: '+ve' })}>+ve</button>
                  </div>
                </div>
                <div className={styles.splitRow}>
                  <div className={styles.surfaceStatic}>Percussion</div>
                  <div className={styles.splitRight}>
                    <button className={modal.percussion==='-ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, percussion: '-ve' })}>-ve</button>
                    <button className={modal.percussion==='+ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, percussion: '+ve' })}>+ve</button>
                  </div>
                </div>
                <div className={styles.splitRow}>
                  <div className={styles.surfaceStatic}>Cold test</div>
                  <div className={styles.splitRight}>
                    <button className={modal.cold_test==='-ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, cold_test: '-ve' })}>-ve</button>
                    <button className={modal.cold_test==='+ve'?styles.testOptSelected:styles.testOpt} onClick={()=> setModal({ ...modal, cold_test: '+ve' })}>+ve</button>
                  </div>
                </div>
              </div>
              <div>
                <div className={styles.endoDxCol}>
                  <select className={styles.select} value={modal.pulpal_dx} onChange={(e) => setModal({ ...modal, pulpal_dx: e.target.value })}>
                    {PULPAL_OPTIONS.map((x) => (<option key={x} value={x}>{x === 'NA' ? 'Pulpal Dx' : x}</option>))}
                  </select>
                  <select className={styles.select} value={modal.apical_dx} onChange={(e) => setModal({ ...modal, apical_dx: e.target.value })}>
                    {APICAL_OPTIONS.map((x) => (<option key={x} value={x}>{x === 'NA' ? 'Apical Dx' : x}</option>))}
                  </select>
                </div>
                <label className={styles.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <input type="checkbox" checked={!!modal.endoSlide} onChange={(e)=> setModal({ ...modal, endoSlide: e.target.checked })} />
                  <span>Add Endodontic Assessment slide for this tooth</span>
                </label>
              </div>
            </div>

            <div className={styles.label}>Proposed Treatment</div>
            <div className={styles.modalRow}>
              <select className={styles.select} value={modal.procedure_id} onChange={async (e) => {
                const val = e.target.value;
                if (val === '__create_procedure__') {
                  const name = prompt('New procedure name');
                  if (name && name.trim()) {
                    try {
                      const created = await apiFetch('/lookups/procedures', { method: 'POST', body: { name } }, getToken);
                      setLookups((prev) => ({ ...prev, procedures: [...prev.procedures, created] }));
                      setModal({ ...modal, procedure_id: String(created.id) });
                    } catch (err) { alert(err.message || 'Failed to create'); }
                  }
                  return;
                }
                setModal({ ...modal, procedure_id: val });
              }}>
                <option value="">Primary Proposed Tx</option>
                {(lookups?.procedures || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                <option value="__create_procedure__">Create…</option>
              </select>
              <select className={styles.select} onChange={async (e)=>{ const id=e.target.value; if(!id) return; if (id==='__create_procedure__'){ const name=prompt('New step name'); if(name&&name.trim()){ try{ const created=await apiFetch('/lookups/procedures',{ method:'POST', body:{ name }}, getToken); setLookups(prev=>({...prev, procedures:[...prev.procedures, created]})); const nm=created.name; if(nm) setModal({...modal, steps:[...modal.steps, nm]}); } catch(err){ alert(err.message||'Failed to create'); } } e.target.value=''; return; } const nm=procName[id]; if(nm) setModal({...modal, steps:[...modal.steps, nm]}); e.target.value=''; }}>
                <option value="">+ Add step…</option>
                {(lookups?.procedures || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                <option value="__create_procedure__">Create…</option>
              </select>
            </div>
            <div className={styles.chips}>
              {modal.steps.map((nm,idx)=> (
                <span key={idx} className={styles.chip}>{nm}<button className={styles.chipBtn} onClick={()=> setModal({...modal, steps: modal.steps.filter((_,i)=>i!==idx)})}>×</button></span>
              ))}
            </div>
            <textarea className={styles.textarea} rows={3} placeholder="Visit steps / notes" value={modal.notes || ''} onChange={(e) => setModal({ ...modal, notes: e.target.value })} />
            <div className={styles.modalActions}>
              {modal.id && (<button className={styles.btn} onClick={async () => { try { await apiFetch(`/plan-items/${modal.id}`, { method: 'DELETE' }, getToken); setRows(rows.filter(r => r.id !== modal.id)); setModal(null);} catch(e){ alert(e.message||'Delete failed'); } }}>Delete</button>)}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={styles.btn} onClick={() => setModal(null)} disabled={saving}>Cancel</button>
                <button className={styles.btnPrimary} onClick={() => {
                  const baseDiag = diagName[modal.diagnosis_id] || '';
                  const diagOverride = [baseDiag, ...modal.extraDiagnoses].filter(Boolean).join(', ');
                  // Persist only additional steps in notes; leave empty when none
                  const stepsChain = modal.steps.length ? modal.steps.join(' -> ') : '';
                  const existingMeta = { ...(rows.find(r => String(r.tooth)===String(modal.tooth))?.meta || {}) };
                  // normalize key name to endo_assessment_slide
                  delete existingMeta['endo assessment slide'];
                  if (modal.endoSlide) existingMeta.endo_assessment_slide = true; else delete existingMeta.endo_assessment_slide;
                  const payload = { ...modal, diagnosis_text_override: diagOverride || null, notes: stepsChain, meta: {
                    ...existingMeta,
                    surfaces: modal.surfaces,
                    palpation: modal.palpation || '',
                    percussion: modal.percussion || '',
                    cold_test: modal.cold_test || '',
                  }};
                  saveOne(payload);
                }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fdiRank(n) {
  const t = parseInt(n, 10);
  if (!t) return 9999;
  const q = Math.floor(t / 10);
  const u = t % 10;
  // Quadrant order: 1 (18..11), 2 (21..28), 3 (38..31), 4 (41..48)
  const quadBase = {1:0,2:100,3:200,4:300}[q] ?? 400;
  let pos;
  if (q === 1) pos = 20 - u;      // 18..11
  else if (q === 2) pos = u;      // 21..28
  else if (q === 3) pos = 20 - u; // 38..31
  else if (q === 4) pos = u;      // 41..48
  else pos = 50 + u;
  return quadBase + pos;
}
